import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link, navigate } from "gatsby";
import { cn, linkToText } from "@/lib/utils";
import { NavbarLinks } from "@/enums/enums";
import { Menu, X } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";

interface NavbarProps {
  type?: "solid" | "transparent";
}

const Navbar: React.FC<NavbarProps> = (props) => {
  const { type = "transparent" } = props;
  const [isScrolled, setIsScrolled] = useState(false);
  const [pathname, setPathname] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hash, setHash] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHash(window.location.hash);
    }
  }, []);


  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    if (type !== "solid") {
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (type !== "solid") {
        window.removeEventListener("scroll", handleScroll);
      }
    };
  }, [type]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setPathname(window.location.pathname);
    }
  }, []);

  const handleLetStart = () => {
    navigate("/contact");
    if (pathname.includes("/contact")) {
      setIsMenuOpen(false);
    }
  };

  const handleNavLinkClick = (link: string) => {
    if (pathname.includes(link) || link === NavbarLinks.SERVICES) {
      setIsMenuOpen(false);
    }
  };

  return (
    <nav
      className={cn(
        "fixed top-0 z-[60] w-full border-b border-white/[.08] group hover:bg-white pointer-events-auto",
        (type === "solid" || isScrolled) && "bg-white border-black/[.08]"
      )}
    >
      <div className="flex h-16 items-center wrap-contain">
        <div className="flex h-full w-full items-center justify-between">
          <div className="flex gap-5 items-center">
            <Link to="/">
              <div className="w-28 md:w-52 flex gap-4">
                <img
                  alt="DevsLane Logo"
                  src="/logo.svg"
                  className={cn(
                    "w-6 md:w-8 filter invert brightness-0 group-hover:invert-0",
                    (type === "solid" || isScrolled) && "invert-0"
                  )}
                />
                <img
                  alt="DevsLane Text"
                  src="/devslane.svg"
                  className={cn(
                    "w-28 md:w-36 filter invert brightness-0 group-hover:invert-0",
                    (type === "solid" || isScrolled) && "invert-0"
                  )}
                />
              </div>
            </Link>

            <span className="hidden lg:block items-center px-4 py-2 bg-gradient-to-r from-pink-100 to-pink-200 text-sm font-medium text-gray-900 rounded-full">
              Now developing faster with AI
            </span>
          </div>

          <div className="flex gap-3 h-full md:gap-12 items-center">
            <div className="hidden h-full md:flex w-fit items-center space-x-8 text-sm font-semibold">
              {Object.values(NavbarLinks).map((link) => (
                <Link
                  key={link}
                  to={`${link === NavbarLinks.SERVICES ? "/#services" : `/${link}`}`}
                  className={cn(
                    "text-white h-full flex items-center group-hover:text-black/60 hover:!text-black",
                    (type === "solid" || isScrolled) && "!text-black/60",
                    pathname === `/${link}/` &&
                      "!text-black -mb-0.5 border-b-2 border-black"
                  )}
                >
                  {linkToText(link)}
                </Link>
              ))}
            </div>

            <Button
              onClick={handleLetStart}
              className="bg-[#0072FF] text-white hover:bg-[#0072FF]/90 rounded-sm px-6"
            >
              Let's Chat
            </Button>
            <div className="md:hidden">
              <Sheet
                open={isMenuOpen}
              >
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "text-white border rounded-full border-white group-hover:text-black",
                      (type === "solid" || isScrolled) &&
                        "!text-black rounded-full bg-surface"
                    )}
                    onClick={(event) => {
                      event.preventDefault();
                      setIsMenuOpen(!isMenuOpen);
                    }}
                  >
                    {isMenuOpen ? (
                      <X className="h-6 w-6" />
                    ) : (
                      <Menu className="h-6 w-6" />
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="top"
                  noBackdrop
                  hideClose
                  className="w-full h-[calc(100vh-4rem)] mt-16 pt-6 bg-white"
                >
                  <div className="flex flex-col space-y-4">
                    {Object.values(NavbarLinks).map((link) => (
                      <Link
                        key={link}
                        to={`${link === NavbarLinks.SERVICES ? "/#services" : `/${link}`}`}
                        onClick={() => handleNavLinkClick(link)}
                        className={cn(
                          "text-lg font-semibold text-black/60 hover:text-black flex items-center px-4 py-2 rounded-md",
                          (pathname.includes(`/${link}/`) || hash.includes(link)) && "!text-black font-bold bg-gray-100"
                        )}
                      >
                        {linkToText(link)}
                      </Link>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
